import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api';
import { authStore, useAuthState } from '@/lib/auth/authStore';
import { lockStore } from '@/lib/auth/lockStore';
import type { LoginInput } from '@/lib/api/auth';

/**
 * Current session + actions.
 *
 *   const { user, isAuthenticated, mustChangePassword, login, logout } = useAuth();
 *
 * `login` rejects with an ApiError (show `error.message`; 429 = throttled, 401 = bad credentials).
 */
export function useAuth() {
  const { user, token, status, reason } = useAuthState();
  const queryClient = useQueryClient();

  const login = useCallback(
    async (input: LoginInput) => {
      const { token: t, user: u } = await authApi.login(input);
      queryClient.clear(); // never show a previous user's cached data
      lockStore.unlock();
      authStore.setSession(t, u);
      return u;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      if (authStore.getState().token) await authApi.logout();
    } catch {
      /* token may already be invalid; sign out locally regardless */
    } finally {
      authStore.clear();
      lockStore.unlock();
      queryClient.clear();
    }
  }, [queryClient]);

  return {
    user,
    token,
    status,
    /** 'expired' when we were signed out by a 401. */
    reason,
    isAuthenticated: status === 'authenticated' && !!user,
    isBootstrapping: status === 'unknown',
    mustChangePassword: !!user?.must_change_password,
    login,
    logout,
  };
}
